var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "nativewind/jsx-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, Text, View, } from 'react-native';
import Markdown from 'react-native-markdown-display';
import IcCalendar from '@/assets/icon/ic_calendar.svg';
import IcParticipants from '@/assets/icon/ic_participants.svg';
import ChallengeStatusBadges from '@/components/ChallengeStatusBadges';
import { ScreenLayout } from '@/components/ScreenLayout';
import useAppComponents from '@/hooks/useAppComponents';
import usePost from '@/hooks/usePost';
import { invalidateChallengeQueries } from '@/utils/challengeQueryCache';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import { isDismissedToday } from '@/atoms/challengeModalAtoms';
import { SccButton } from '@/components/atoms';
import { SafeAreaWrapper } from '@/components/SafeAreaWrapper';
import SccTouchableOpacity from '@/components/SccTouchableOpacity';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import { ChallengeDateFormat } from '@/utils/ChallengeDateFormat';
import { useCheckAuth } from '@/utils/checkAuth';
import { cn } from '@/utils/cn';
import ChallengeDetailCompanyModal from './components/ChallengeDetailCompanyModal';
import ChallengeDetailPasscodeBottomSheet from './components/ChallengeDetailPasscodeBottomSheet';
import ChallengeDetailRankSection from './components/ChallengeDetailRankSection/ChallengeDetailRankSection';
import ChallengeDetailStatus from './components/ChallengeDetailStatus';
import ChallengeDetailStickyActionBar from './components/ChallengeDetailStickyActionBar';
import ChallengeWelcomeModal from './components/ChallengeWelcomeModal';
import LastMonthRankingModal from './components/LastMonthRankingModal';
const ChallengeDetailScreen = ({ route, navigation, }) => {
    var _a, _b, _c, _d, _e, _f;
    const { challengeId, autoJoin, passcode: initialPasscode } = route.params;
    const shouldAutoJoin = autoJoin === 'true';
    const checkAuth = useCheckAuth();
    const { api } = useAppComponents();
    const [showPasscodeBottomSheet, setShowPasscodeBottomSheet] = useState(false);
    const [showCompanyModal, setShowCompanyModal] = useState(false);
    const [passcode, setPasscode] = useState(initialPasscode);
    const [isDescriptionCollapsed, setIsDescriptionCollapsed] = useState(false);
    const [showLastMonthRankingModal, setShowLastMonthRankingModal] = useState(false);
    const { data } = useQuery({
        queryKey: ['ChallengeDetail', challengeId],
        queryFn: (_a) => __awaiter(void 0, [_a], void 0, function* ({ queryKey }) { return (yield api.getChallengePost({ challengeId: queryKey[1] })).data; }),
    });
    const challenge = data === null || data === void 0 ? void 0 : data.challenge;
    const ranks = (_a = data === null || data === void 0 ? void 0 : data.ranks) !== null && _a !== void 0 ? _a : [];
    const myRank = data === null || data === void 0 ? void 0 : data.myRank;
    const hasJoined = data === null || data === void 0 ? void 0 : data.hasJoined;
    const hasPasscode = (_b = data === null || data === void 0 ? void 0 : data.hasPasscode) !== null && _b !== void 0 ? _b : false;
    const isB2B = (_c = data === null || data === void 0 ? void 0 : data.isB2B) !== null && _c !== void 0 ? _c : false;
    const queryClient = useQueryClient();
    const joinChallenge = usePost(['ChallengeDetail', challengeId], (params) => __awaiter(void 0, void 0, void 0, function* () {
        const result = yield api.joinChallengePost(params);
        return result.data;
    }));
    // 참여 성공 시 홈 진척 카드·남은 매장 목록도 갱신한다. usePost 는 mutationKey
    // (= ['ChallengeDetail', challengeId]) 하나만 무효화해서, 참여 직후 홈으로 나가면
    // staleTime 5분짜리 HomeScreenData 가 옛 값이라 '{브랜드} 정복하기' 카드가 안 뜬다.
    // 참여 호출부가 4곳(자동참여·CTA·B2B·패스코드)이라 호출부마다 붙이지 않고 여기 한 곳에서 건다.
    useEffect(() => {
        if (joinChallenge.isSuccess) {
            invalidateChallengeQueries(queryClient);
        }
    }, [joinChallenge.isSuccess, queryClient]);
    // 챌린지명이 아니라 '챌린지' 고정 (시안 반영, 24:5368)
    useEffect(() => {
        navigation.setOptions({ headerTitle: '챌린지' });
    }, [navigation]);
    // 딥링크 autoJoin=true → 자동 참여 처리
    const autoJoinHandled = useRef(false);
    useEffect(() => {
        if (!shouldAutoJoin || autoJoinHandled.current) {
            return;
        }
        if (hasJoined === undefined || !data) {
            return; // 아직 데이터 로딩 중
        }
        if (hasJoined) {
            return; // 이미 참여한 챌린지
        }
        checkAuth(() => {
            autoJoinHandled.current = true;
            if (isB2B) {
                setShowCompanyModal(true);
                return;
            }
            joinChallenge.mutate({
                challengeId,
                passcode: initialPasscode,
            });
        });
    }, [
        shouldAutoJoin,
        hasJoined,
        data,
        isB2B,
        challengeId,
        checkAuth,
        initialPasscode,
    ]);
    // 새 환영팝업(welcomePopup 지정) 또는 B2B 기존팝업 중 하나라도 뜰 조건이면
    // LastMonthRankingModal은 표시하지 않는다(한 번에 하나만).
    const shouldShowWelcomeModal = joinChallenge.isSuccess && ((challenge === null || challenge === void 0 ? void 0 : challenge.welcomePopup) != null || isB2B);
    useEffect(() => {
        if (hasJoined &&
            (challenge === null || challenge === void 0 ? void 0 : challenge.modalImageUrl) &&
            !isDismissedToday(challengeId) &&
            !shouldShowWelcomeModal // 한 번에 하나의 모달만 띄운다.
        ) {
            setShowLastMonthRankingModal(true);
        }
    }, [
        hasJoined,
        challenge === null || challenge === void 0 ? void 0 : challenge.modalImageUrl,
        challengeId,
        shouldShowWelcomeModal,
    ]);
    const prevY = useRef(0);
    const [visible, setVisible] = useState(false);
    const onScroll = (e) => {
        if (!hasJoined) {
            return;
        }
        const y = e.nativeEvent.contentOffset.y;
        const diff = y - prevY.current;
        if (y <= 0) {
            setVisible(false);
            prevY.current = y;
            return;
        }
        const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
        const isAtBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 1;
        if (isAtBottom) {
            setVisible(true);
            prevY.current = y;
            return;
        }
        if (Math.abs(diff) < 8)
            return;
        if (diff > 0) {
            setVisible(true);
        }
        else {
            setVisible(false);
        }
        prevY.current = y;
    };
    // ctpl 챌린지는 '남은 매장 보기'가 sticky CTA 역할을 대신하므로 그만큼 노출 조건을 좁힌다.
    const shouldShowStickyActionBar = hasJoined === true &&
        (challenge === null || challenge === void 0 ? void 0 : challenge.status) !== 'Closed' &&
        !(challenge === null || challenge === void 0 ? void 0 : challenge.hasConquerTargetPlaceList);
    return (_jsx(LogParamsProvider, { params: { challenge_id: challengeId }, children: _jsxs(ScreenLayout, { isHeaderVisible: false, children: [_jsxs(ScrollView, { className: "bg-white", onScroll: onScroll, scrollEventThrottle: 16, children: [_jsxs(View, { className: "pl-[25px] pr-[20px] pt-[20px] pb-[8px] gap-[8px]", children: [_jsxs(View, { className: "gap-[4px]", children: [_jsx(ChallengeStatusBadges, { status: [(_d = challenge === null || challenge === void 0 ? void 0 : challenge.status) !== null && _d !== void 0 ? _d : 'Closed'], isMyChallenge: hasJoined !== null && hasJoined !== void 0 ? hasJoined : false }), _jsx(Text, { className: "text-[24px] leading-[34px] font-pretendard-bold text-black", children: challenge === null || challenge === void 0 ? void 0 : challenge.name })] }), challenge && (_jsxs(View, { className: "flex-row items-center gap-[20px]", children: [_jsxs(View, { className: "flex-row items-center gap-[4px]", children: [_jsx(IcCalendar, { width: 20, height: 20 }), _jsx(Text, { className: "text-[15px] leading-[22px] tracking-[-0.3px] font-pretendard-regular text-gray-v2-60", children: ChallengeDateFormat.formatChallengeDetailPeriod(challenge.startsAt, challenge.endsAt) })] }), _jsxs(View, { className: "flex-row items-center gap-[4px]", children: [_jsx(IcParticipants, { width: 20, height: 20 }), _jsx(Text, { className: "text-[15px] leading-[22px] tracking-[-0.3px] font-pretendard-regular text-gray-v2-60", children: `${challenge.participationsCount}명` })] })] }))] }), challenge && (_jsxs(_Fragment, { children: [_jsx(ChallengeDetailStatus, { challenge: challenge }), challenge.description ? (_jsx(DescriptionRenderer, { description: challenge.description, isCollapsed: isDescriptionCollapsed, onToggleCollapse: () => setIsDescriptionCollapsed(!isDescriptionCollapsed) })) : (_jsx(Text, { className: "px-[25px] mt-[25px] mb-[60px] text-[16px] leading-[26px] font-pretendard-regular text-black", children: `${challenge.name} 챌린지에서 ${challenge.goal}개 장소 정복에 도전해보세요!${!isEmpty(challenge.milestones)
                                        ? ` 중간목표 ${challenge.milestones[0]}개를 달성하면 콩알이 친구가 도전을 함께 하게 됩니다🤗`
                                        : ''}` })), challenge.hasConquerTargetPlaceList && hasJoined && (_jsx(View, { className: "px-[25px] pb-[32px]", children: _jsx(SccTouchableOpacity, { elementName: "challenge_detail_conquer_target_places", activeOpacity: 0.8, className: "border-[1.5px] border-brand-40 rounded-[8px] px-[28px] py-[12px] items-center justify-center", onPress: () => navigation.navigate('ChallengeConquerTargetPlaces', {
                                            challengeId,
                                            initialViewMode: 'map',
                                        }), children: _jsx(Text, { className: "text-[16px] leading-[24px] tracking-[-0.32px] font-pretendard-semibold text-brand-40", children: "\uB0A8\uC740 \uB9E4\uC7A5 \uBCF4\uAE30" }) }) }))] })), hasJoined && (_jsx(ChallengeDetailRankSection, { ranks: ranks, myRank: myRank, quests: data === null || data === void 0 ? void 0 : data.quests, lastMonthRankImageUrl: challenge === null || challenge === void 0 ? void 0 : challenge.lastMonthRankImageUrl }))] }), shouldShowStickyActionBar && (_jsx(ChallengeDetailStickyActionBar, { visible: visible, onGoConquer: () => 
                    // Search는 Main tab 내부에 있어 nested navigate 필요.
                    // initSortOption은 지정하지 않음 → 다른 검색 진입과 동일하게
                    // filterAtom default(LOW_SCORE = 접근레벨낮은순) 적용.
                    navigation.navigate('Main', {
                        screen: 'Search',
                        params: {
                            initKeyword: '',
                        },
                    }) })), hasJoined === false && (_jsx(SafeAreaWrapper, { edges: ['bottom'], children: _jsx(View, { className: "px-[20px] pt-[20px] pb-[12px] bg-white", children: _jsx(SccButton, { text: '챌린지 참여하기', textColor: "white", buttonColor: "brandColor", fontFamily: font.pretendardBold, onPress: () => {
                                checkAuth(() => {
                                    if (hasPasscode || isB2B) {
                                        setShowPasscodeBottomSheet(true);
                                    }
                                    else {
                                        joinChallenge.mutate({ challengeId });
                                    }
                                });
                            }, elementName: "challenge_detail_join" }) }) })), _jsx(ChallengeDetailCompanyModal, { isVisible: showCompanyModal, onPressCloseButton: () => {
                        setShowCompanyModal(false);
                        setPasscode(undefined);
                    }, onPressConfirmButton: companyInfo => {
                        setShowCompanyModal(false);
                        joinChallenge.mutate({
                            challengeId,
                            passcode,
                            companyInfo,
                        });
                    }, formSchema: challenge === null || challenge === void 0 ? void 0 : challenge.b2bFormSchema }), _jsx(ChallengeDetailPasscodeBottomSheet, { isVisible: showPasscodeBottomSheet, onPressCloseButton: () => {
                        setShowPasscodeBottomSheet(false);
                    }, onPressConfirmButton: _passcode => {
                        setShowPasscodeBottomSheet(false);
                        if (isB2B) {
                            setShowCompanyModal(true);
                            setPasscode(_passcode);
                            return;
                        }
                        joinChallenge.mutate({ challengeId, passcode: _passcode });
                    } }), _jsx(ChallengeWelcomeModal, { visible: shouldShowWelcomeModal, imageUrl: (_e = challenge === null || challenge === void 0 ? void 0 : challenge.welcomePopup) === null || _e === void 0 ? void 0 : _e.imageUrl, description: (_f = challenge === null || challenge === void 0 ? void 0 : challenge.welcomePopup) === null || _f === void 0 ? void 0 : _f.description }), (challenge === null || challenge === void 0 ? void 0 : challenge.modalImageUrl) && (_jsx(LastMonthRankingModal, { challengeId: challengeId, imageUrl: challenge.modalImageUrl, visible: showLastMonthRankingModal, onClose: () => setShowLastMonthRankingModal(false) }))] }) }));
};
export default ChallengeDetailScreen;
const DESCRIPTION_COLLAPSE_THRESHOLD = 50;
const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\(([^)]+)\)/;
const DescriptionRenderer = ({ description, isCollapsed, onToggleCollapse, }) => {
    // description을 문단 단위로 분리
    const paragraphs = description.split('\n');
    const lastParagraph = paragraphs[paragraphs.length - 1];
    // 마지막 문단에 링크가 있는지 확인 (Markdown 링크 패턴: [text](url))
    const hasLinkInLastParagraph = MARKDOWN_LINK_REGEX.test(lastParagraph);
    let mainContent;
    let linkParagraph = null;
    if (hasLinkInLastParagraph) {
        // 마지막 문단에 링크가 있으면 분리
        mainContent = paragraphs.slice(0, -1).join('\n');
        linkParagraph = lastParagraph;
    }
    else {
        // 링크가 없으면 전체를 mainContent로 사용
        mainContent = description;
    }
    // mainContent의 길이를 체크하여 접기/더보기 필요 여부 결정
    const shouldShowToggle = mainContent.length >= DESCRIPTION_COLLAPSE_THRESHOLD;
    // react-native-markdown-display 는 style prop 으로 style 객체만 받는 서드파티라
    // NativeWind className 대응이 안 된다 (기존 동작 그대로 유지).
    const markdownStyle = {
        body: {
            lineHeight: 26,
            fontSize: 16,
            letterSpacing: -0.08,
            color: color.gray85,
            fontFamily: font.pretendardRegular,
        },
        link: {
            color: color.brand60,
            fontSize: 16,
            lineHeight: 26,
            textDecorationLine: 'underline',
            fontFamily: font.pretendardMedium,
        },
    };
    return (_jsx(View, { className: "pt-[24px] pb-[12px]", children: _jsxs(View, { className: "mx-[20px] p-[16px] gap-[4px] bg-gray-v2-10 rounded-[12px]", children: [_jsxs(View, { children: [_jsx(Markdown, { style: markdownStyle, children: isCollapsed && shouldShowToggle
                                ? mainContent.substring(0, DESCRIPTION_COLLAPSE_THRESHOLD) + '...'
                                : mainContent }), shouldShowToggle && (_jsx(SccTouchableOpacity, { elementName: "challenge_description_toggle", onPress: onToggleCollapse, className: "items-end mt-[4px]", children: _jsx(Text, { className: cn('text-[14px] font-pretendard-regular text-gray-40', isCollapsed && 'underline'), children: isCollapsed ? '더보기' : '접기' }) }))] }), linkParagraph && (_jsx(Markdown, { style: markdownStyle, children: linkParagraph }))] }) }));
};
